import { useCallback, useMemo, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Drawer from '@mui/material/Drawer';
import Stack from '@mui/material/Stack';
import SvgIcon from '@mui/material/SvgIcon';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import toast from 'react-hot-toast';

import { Logo, LogoFull } from 'src/components/logo';
import { RouterLink } from 'src/components/router-link';
import { Scrollbar } from 'src/components/scrollbar';
import { usePathname } from 'src/hooks/use-pathname';
import { paths } from 'src/paths';
import { SideNavSection } from './side-nav-section';
import LogOut03 from '@untitled-ui/icons-react/build/esm/LogOut03';
import { useRouter } from 'src/hooks/use-router';
import { useAuth } from 'src/hooks/use-auth';
import { Issuer } from 'src/utils/auth';
import { useMockedUser } from 'src/hooks/use-mocked-user';
import { AccountButton } from '../account-button';
import { Divider, Tooltip, IconButton } from '@mui/material';
import Grid from '@mui/material/Grid2';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import { SettingsContext } from 'src/contexts/settings';

const SIDE_NAV_WIDTH = 280;

const useCssVars = (color) => {
  const theme = useTheme();

  return useMemo(() => {
    switch (color) {
      case 'blend-in':
        if (theme.palette.mode === 'dark') {
          return {
            '--nav-bg': theme.palette.background.default,
            '--nav-color': theme.palette.neutral[100],
            '--nav-border-color': theme.palette.neutral[700],
            '--nav-logo-border': theme.palette.neutral[700],
            '--nav-section-title-color': theme.palette.neutral[400],
            '--nav-item-color': theme.palette.neutral[400],
            '--nav-item-hover-bg': 'rgba(255, 255, 255, 0.04)',
            '--nav-item-active-bg': 'rgba(255, 255, 255, 0.04)',
            '--nav-item-active-color': theme.palette.text.primary,
            '--nav-item-disabled-color': theme.palette.neutral[600],
            '--nav-item-icon-color': theme.palette.neutral[500],
            '--nav-item-icon-active-color': theme.palette.primary.main,
            '--nav-item-icon-disabled-color': theme.palette.neutral[700],
            '--nav-item-chevron-color': theme.palette.neutral[700],
            '--nav-scrollbar-color': theme.palette.neutral[400],
          };
        } else {
          return {
            '--nav-bg': theme.palette.background.default,
            '--nav-color': theme.palette.text.primary,
            '--nav-border-color': theme.palette.neutral[100],
            '--nav-logo-border': theme.palette.neutral[100],
            '--nav-section-title-color': theme.palette.neutral[400],
            '--nav-item-color': theme.palette.text.secondary,
            '--nav-item-hover-bg': theme.palette.action.hover,
            '--nav-item-active-bg': theme.palette.action.selected,
            '--nav-item-active-color': theme.palette.text.primary,
            '--nav-item-disabled-color': theme.palette.neutral[400],
            '--nav-item-icon-color': theme.palette.neutral[400],
            '--nav-item-icon-active-color': theme.palette.primary.main,
            '--nav-item-icon-disabled-color': theme.palette.neutral[400],
            '--nav-item-chevron-color': theme.palette.neutral[400],
            '--nav-scrollbar-color': theme.palette.neutral[900],
          };
        }

      case 'discrete':
        if (theme.palette.mode === 'dark') {
          return {
            '--nav-bg': theme.palette.neutral[900],
            '--nav-color': theme.palette.neutral[100],
            '--nav-border-color': theme.palette.neutral[700],
            '--nav-logo-border': theme.palette.neutral[700],
            '--nav-section-title-color': theme.palette.neutral[400],
            '--nav-item-color': theme.palette.neutral[400],
            '--nav-item-hover-bg': 'rgba(255, 255, 255, 0.04)',
            '--nav-item-active-bg': 'rgba(255, 255, 255, 0.04)',
            '--nav-item-active-color': theme.palette.text.primary,
            '--nav-item-disabled-color': theme.palette.neutral[600],
            '--nav-item-icon-color': theme.palette.neutral[500],
            '--nav-item-icon-active-color': theme.palette.primary.main,
            '--nav-item-icon-disabled-color': theme.palette.neutral[700],
            '--nav-item-chevron-color': theme.palette.neutral[700],
            '--nav-scrollbar-color': theme.palette.neutral[400],
          };
        } else {
          return {
            '--nav-bg': theme.palette.neutral[50],
            '--nav-color': theme.palette.text.primary,
            '--nav-border-color': theme.palette.divider,
            '--nav-logo-border': theme.palette.neutral[200],
            '--nav-section-title-color': theme.palette.neutral[500],
            '--nav-item-color': theme.palette.neutral[500],
            '--nav-item-hover-bg': theme.palette.action.hover,
            '--nav-item-active-bg': theme.palette.action.selected,
            '--nav-item-active-color': theme.palette.text.primary,
            '--nav-item-disabled-color': theme.palette.neutral[400],
            '--nav-item-icon-color': theme.palette.neutral[400],
            '--nav-item-icon-active-color': theme.palette.primary.main,
            '--nav-item-icon-disabled-color': theme.palette.neutral[400],
            '--nav-item-chevron-color': theme.palette.neutral[400],
            '--nav-scrollbar-color': theme.palette.neutral[900],
          };
        }

      case 'evident':
        if (theme.palette.mode === 'dark') {
          return {
            '--nav-bg': '#070C17',
            '--nav-color': '#FFFFFF',
            '--nav-border-color': 'transparent',
            '--nav-logo-border': 'transparent',
            '--nav-section-title-color': 'rgba(255,255,255,0.35)',
            '--nav-item-color': 'rgba(255,255,255,0.55)',
            '--nav-item-hover-bg': 'rgba(255, 255, 255, 0.06)',
            '--nav-item-active-bg': 'rgba(255, 146, 85, 0.12)',
            '--nav-item-active-color': '#FF9255',
            '--nav-item-disabled-color': 'rgba(255,255,255,0.25)',
            '--nav-item-icon-color': 'rgba(255,255,255,0.45)',
            '--nav-item-icon-active-color': '#FF9255',
            '--nav-item-icon-disabled-color': 'rgba(255,255,255,0.2)',
            '--nav-item-chevron-color': 'rgba(255,255,255,0.35)',
            '--nav-scrollbar-color': 'rgba(255,255,255,0.15)',
          };
        } else {
          return {
            '--nav-bg': '#F8FAFB',
            '--nav-color': '#111927',
            '--nav-border-color': '#E5E7EB',
            '--nav-logo-border': 'transparent',
            '--nav-section-title-color': 'rgba(0,0,0,0.35)',
            '--nav-item-color': 'rgba(0,0,0,0.55)',
            '--nav-item-hover-bg': 'rgba(33, 81, 197, 0.06)',
            '--nav-item-active-bg': 'rgba(33, 81, 197, 0.1)',
            '--nav-item-active-color': '#2151C5',
            '--nav-item-disabled-color': 'rgba(0,0,0,0.25)',
            '--nav-item-icon-color': 'rgba(0,0,0,0.45)',
            '--nav-item-icon-active-color': '#2151C5',
            '--nav-item-icon-disabled-color': 'rgba(0,0,0,0.2)',
            '--nav-item-chevron-color': 'rgba(0,0,0,0.35)',
            '--nav-scrollbar-color': 'rgba(0,0,0,0.2)',
          };
        }

      default:
        return {};
    }
  }, [theme, color]);
};

export const SideNav = (props) => {
  const { color = 'evident', sections = [] } = props;
  const theme = useTheme();
  const pathname = usePathname();
  const cssVars = useCssVars(color);
  const router = useRouter();
  const auth = useAuth();
  const user = useMockedUser();
  const { paletteMode, handleToggleTheme } = useContext(SettingsContext);

  const handleLogout = useCallback(async () => {
    try {
      switch (auth.issuer) {
        case Issuer.JWT: {
          await auth.signOut();
          break;
        }

        default: {
          console.warn('Using an unknown Auth Issuer, did not log out');
        }
      }

      router.push(paths.index);
    } catch (err) {
      console.error(err);
      toast.error(
        'No pudimos cerrar la sesión. Intenta nuevamente o contacta con el soporte si el problema persiste.',
        { duration: 5000, position: 'top-center' }
      );
    }
  }, [auth, router]);

  return (
    <Drawer
      anchor="left"
      open
      PaperProps={{
        sx: {
          ...cssVars,
          backgroundColor: 'var(--nav-bg)',
          borderRightColor: 'var(--nav-border-color)',
          borderRightStyle: 'solid',
          borderRightWidth: 1,
          color: 'var(--nav-color)',
          width: SIDE_NAV_WIDTH,
        },
      }}
      variant="permanent"
    >
      <Scrollbar
        sx={{
          height: '100%',
          '& .simplebar-content': {
            height: '100%',
          },
          '& .simplebar-scrollbar:before': {
            background: 'var(--nav-scrollbar-color)',
          },
        }}
      >
        <Stack sx={{ height: '100%' }}>
          <Stack
            alignItems="center"
            direction="row"
            sx={{ p: 3 }}
          >
            <Box
              component={RouterLink}
              href={paths.index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                py: 1,
                width: '100%',
              }}
            >
              <LogoFull height={36} />
            </Box>
          </Stack>

          <Stack
            component="nav"
            spacing={2}
            sx={{
              flexGrow: 1,
              px: 2,
            }}
          >
            {sections.map((section, index) => (
              <SideNavSection
                items={section.items}
                key={index}
                pathname={pathname}
                subheader={section.subheader}
              />
            ))}
          </Stack>
          <Box sx={{ p: 2 }}>
            <Divider />
            <Box
              component="div"
              sx={{
                p: 2,
                mx: 0,
                my: 2,
                borderRadius: 1,
                boxShadow: 1,
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                backgroundColor: 'background.paper',
                borderColor: 'primary.main',
                borderWidth: '1px',
                borderStyle: 'solid',
                '&:hover': {
                  backgroundColor: 'background.default',
                },
              }}
            >
              <Grid
                container
                spacing={2}
                alignItems="center"
              >
                <Grid size={{ xs: 10 }}>
                  <Tooltip title={user?.name + ' ' + user?.lastname}>
                    <Typography
                      sx={{ cursor: 'pointer' }}
                      variant="body1"
                      color="text.secondary"
                      style={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: '160px',
                      }}
                    >
                      {user?.name + ' ' + user?.lastname}
                    </Typography>
                  </Tooltip>
                  <Tooltip title={user?.email}>
                    <Typography
                      sx={{ cursor: 'pointer' }}
                      variant="body2"
                      color="text.secondary"
                      style={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: '160px',
                      }}
                    >
                      {user?.email}
                    </Typography>
                  </Tooltip>
                </Grid>
                <Grid
                  size={{ xs: 2 }}
                  container
                  justifyContent="flex-end"
                >
                  <AccountButton />
                </Grid>
              </Grid>
            </Box>
            <Stack
              direction="row"
              spacing={1}
              sx={{ mb: 1 }}
            >
              <Button
                fullWidth
                startIcon={
                  <SvgIcon fontSize="small">
                    {paletteMode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
                  </SvgIcon>
                }
                onClick={handleToggleTheme}
                variant="outlined"
              >
                {paletteMode === 'dark' ? 'Modo Claro' : 'Modo Oscuro'}
              </Button>
            </Stack>
            <Button
              fullWidth
              startIcon={
                <SvgIcon fontSize="small">
                  <LogOut03 />
                </SvgIcon>
              }
              onClick={handleLogout}
              target="_blank"
              variant="outlined"
            >
              Cerrar sesión
            </Button>
          </Box>
        </Stack>
      </Scrollbar>
    </Drawer>
  );
};

SideNav.propTypes = {
  color: PropTypes.oneOf(['blend-in', 'discrete', 'evident']),
  sections: PropTypes.array,
  lgUp: PropTypes.bool,
  onClose: PropTypes.func,
};
